import React, { useEffect, useRef, useState } from 'react';
import { formatDateTime } from '../../utils/formatters';
import StatusBadge from '../common/StatusBadge';
import {
  createCheckoutSetupSession,
  disableUser,
  enableUser,
  fetchBillingPlans,
  fetchDaysGrant,
  fetchProfileHistory,
  fetchUserSessions,
  updateDaysGrant,
} from '../../api/adminClient';

const PROFILE_HISTORY_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'firstname', label: 'First name' },
  { key: 'lastname', label: 'Last name' },
  { key: 'company', label: 'Company' },
  { key: 'jobtitle', label: 'Job title' },
  { key: 'practicestate', label: 'Practice state' },
  { key: 'phoneno', label: 'Phone' },
  { key: 'address', label: 'Address' },
  { key: 'zipcode', label: 'Zip code' },
];

const getUserProfiles = (user) => {
  if (Array.isArray(user?.profiles) && user.profiles.length > 0) {
    return user.profiles;
  }
  if (user?.profile && typeof user.profile === 'object') {
    return [user.profile];
  }
  return [];
};

const formatProfileValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return String(value);
};

const isInitialProfileEntry = (oldData) =>
  !oldData ||
  PROFILE_HISTORY_FIELDS.every(({ key }) => oldData[key] == null);

const getProfileHistoryChanges = (oldData, newData) =>
  PROFILE_HISTORY_FIELDS.filter(({ key }) => (oldData?.[key] ?? null) !== (newData?.[key] ?? null)).map(
    ({ key, label }) => ({
      label,
      old: formatProfileValue(oldData?.[key]),
      new: formatProfileValue(newData?.[key]),
    })
  );

const formatProfileSummary = (profile) => {
  const parts = [
    profile?.name,
    profile?.company,
    profile?.jobtitle,
    profile?.practicestate,
    profile?.phoneno,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'No profile details';
};


const formatAgreement = (value) => {
  if (value === true) {
    return 'Yes';
  }
  if (value === false) {
    return 'No';
  }
  return '—';
};

const UserManagement = ({ users = [], loading, pagination, statusFilter = 'all', sortFilter = 'createdAt_desc', onStatusChange, onPageChange, onSearchChange, onDateChange, onSortChange, onRefreshUsers }) => {
  const [search, setSearch] = useState('');
  const [dateSearch, setDateSearch] = useState('');
  const debounceRef = useRef(null);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpData, setOtpData] = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [showGrantTrialModal, setShowGrantTrialModal] = useState(false);
  const [grantTrialLoading, setGrantTrialLoading] = useState(false);
  const [grantTrialSaving, setGrantTrialSaving] = useState(false);
  const [grantTrialMonths, setGrantTrialMonths] = useState('');
  const [selectedProfileUuid, setSelectedProfileUuid] = useState('');
  const [showChargeCustomerModal, setShowChargeCustomerModal] = useState(false);
  const [chargeCustomerLoading, setChargeCustomerLoading] = useState(false);
  const [chargeCustomerSubmitting, setChargeCustomerSubmitting] = useState(false);
  const [billingPlans, setBillingPlans] = useState([]);
  const [selectedPlanUuid, setSelectedPlanUuid] = useState('');
  const [selectedUserUuid, setSelectedUserUuid] = useState('');
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsData, setVersionsData] = useState(null);
  const [togglingUuid, setTogglingUuid] = useState(null);

  const handleToggleEnabled = async (user) => {
    const isEnabled = user.isEnabled !== false;
    const verb = isEnabled ? 'disable' : 'enable';
    if (!window.confirm(
      isEnabled
        ? `Disable ${user.emailAddress}? They will not be able to log in until re-enabled.`
        : `Re-enable ${user.emailAddress}? They will be able to log in again.`
    )) {
      return;
    }

    setTogglingUuid(user.uuid);
    try {
      await (isEnabled ? disableUser(user.uuid) : enableUser(user.uuid));
      onRefreshUsers?.();
    } catch (error) {
      console.error(`Failed to ${verb} user:`, error);
      window.alert(`Failed to ${verb} user.`);
    } finally {
      setTogglingUuid(null);
    }
  };

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange?.(search);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewOtp = async (userUuid) => {
    setOtpLoading(true);
    setShowOtpModal(true);
    setOtpData(null);

    try {
      const response = await fetchUserSessions(userUuid);
      setOtpData(response.data);
    } catch (error) {
      console.error('Failed to fetch OTPs:', error);
      setOtpData({ error: 'Failed to load OTPs' });
    } finally {
      setOtpLoading(false);
    }
  };

  const closeOtpModal = () => {
    setShowOtpModal(false);
    setOtpData(null);
  };

  const openVersionsModal = async (profile, user) => {
    const profileUuid = profile?.uuid;
    if (!profileUuid) {
      window.alert('Profile UUID not found.');
      return;
    }

    setShowVersionsModal(true);
    setVersionsLoading(true);
    setVersionsData(null);

    try {
      const response = await fetchProfileHistory(profileUuid);
      setVersionsData({
        ...response.data,
        profileLabel: formatProfileSummary(profile),
        userEmail: user?.emailAddress || response.data?.userEmail,
      });
    } catch (error) {
      console.error('Failed to fetch profile history:', error);
      const apiError = error?.response?.data?.error;
      setVersionsData({
        error: apiError || 'Failed to load profile history',
        userEmail: user?.emailAddress,
        profileLabel: formatProfileSummary(profile),
      });
    } finally {
      setVersionsLoading(false);
    }
  };

  const closeVersionsModal = () => {
    setShowVersionsModal(false);
    setVersionsLoading(false);
    setVersionsData(null);
  };

  const openGrantTrialModal = async (user) => {
    const profileUuid = user?.profile?.uuid || user?.profileUuid || '';
    if (!profileUuid) {
      window.alert('Profile UUID not found for this user.');
      return;
    }

    setSelectedProfileUuid(profileUuid);
    setGrantTrialMonths('');
    setShowGrantTrialModal(true);
    setGrantTrialLoading(true);

    try {
      const response = await fetchDaysGrant(profileUuid);
      const monthsFromApi = Number(response.data?.data?.days_grant);
      setGrantTrialMonths(
        Number.isFinite(monthsFromApi) ? String(monthsFromApi) : '0'
      );
    } catch (error) {
      console.error('Failed to fetch trial months grant:', error);
      setGrantTrialMonths('0');
      window.alert('Failed to load current trial months.');
    } finally {
      setGrantTrialLoading(false);
    }
  };

  const closeGrantTrialModal = () => {
    if (grantTrialSaving) {
      return;
    }
    setShowGrantTrialModal(false);
    setGrantTrialLoading(false);
    setGrantTrialSaving(false);
    setGrantTrialMonths('');
    setSelectedProfileUuid('');
  };

  const submitGrantTrial = async () => {
    const parsedMonths = Number(grantTrialMonths);
    if (!selectedProfileUuid) {
      window.alert('Missing profile UUID.');
      return;
    }

    if (!Number.isInteger(parsedMonths) || parsedMonths < 1 || parsedMonths > 2) {
      window.alert('Please enter a number of months (1-2).');
      return;
    }

    setGrantTrialSaving(true);
    try {
      await updateDaysGrant({
        profileUuid: selectedProfileUuid,
        days_grant: parsedMonths,
      });
      window.alert('Trial granted successfully.');
      closeGrantTrialModal();
      onRefreshUsers?.();
    } catch (error) {
      console.error('Failed to update trial months grant:', error);
      window.alert('Failed to grant trial months.');
    } finally {
      setGrantTrialSaving(false);
    }
  };

  const getDefaultRedirectUrls = (userUuid) => {
    if (typeof window === 'undefined') {
      return {
        success: '',
        cancel: '',
      };
    }
    const baseUrl = `${window.location.origin}/admin/users`;
    const userParam = userUuid ? `&user=${encodeURIComponent(userUuid)}` : '';
    return {
      success: `${baseUrl}?billing=success${userParam}`,
      cancel: `${baseUrl}?billing=cancelled${userParam}`,
    };
  };

  const openChargeCustomerModal = async (user) => {
    const userUuid = user?.uuid || '';
    if (!userUuid) {
      window.alert('User UUID not found.');
      return;
    }

    const defaults = getDefaultRedirectUrls(userUuid);
    setSelectedUserUuid(userUuid);
    setSelectedPlanUuid('');
    setBillingPlans([]);
    setShowChargeCustomerModal(true);
    setChargeCustomerLoading(true);

    try {
      const response = await fetchBillingPlans();
      const plans = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setBillingPlans(plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      window.alert('Failed to load plans.');
    } finally {
      setChargeCustomerLoading(false);
    }
  };

  const closeChargeCustomerModal = () => {
    if (chargeCustomerSubmitting) {
      return;
    }
    setShowChargeCustomerModal(false);
    setChargeCustomerLoading(false);
    setChargeCustomerSubmitting(false);
    setBillingPlans([]);
    setSelectedPlanUuid('');
    setSelectedUserUuid('');
  };

  const submitChargeCustomer = async () => {
    if (!selectedUserUuid) {
      window.alert('User not found.');
      return;
    }
    if (!selectedPlanUuid) {
      window.alert('Please choose a plan.');
      return;
    }

    const defaultRedirects = getDefaultRedirectUrls(selectedUserUuid);
    setChargeCustomerSubmitting(true);
    try {
      const response = await createCheckoutSetupSession(selectedUserUuid, {
        planUuid: selectedPlanUuid,
        successUrl: defaultRedirects.success || undefined,
        cancelUrl: defaultRedirects.cancel || undefined,
      });
      const checkoutUrl = response?.data?.url;
      if (!checkoutUrl) {
        throw new Error('Checkout URL missing in response.');
      }
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to create checkout setup session:', error);
      const apiError = error?.response?.data?.error;
      window.alert(apiError || 'Failed to start billing checkout.');
      setChargeCustomerSubmitting(false);
    }
  };

  const totalUsers = pagination?.total ?? 0;
  const currentPage = pagination?.page ?? 1;
  const pageLimit = pagination?.limit ?? 10;
  const totalPages = pagination?.pages ?? 1;
  const hasUsers = users.length > 0;
  const rangeStart = hasUsers ? ((currentPage - 1) * pageLimit) + 1 : 0;
  const rangeEnd = hasUsers ? Math.min(((currentPage - 1) * pageLimit) + users.length, totalUsers) : 0;

  return (
    <>
      {showOtpModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">User OTP Sessions</h5>
                <button type="button" className="btn-close" onClick={closeOtpModal}></button>
              </div>
              <div className="modal-body">
                {otpLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : otpData?.error ? (
                  <div className="alert alert-danger">{otpData.error}</div>
                ) : otpData ? (
                  <>
                    <div className="mb-3">
                      <p className="mb-1"><strong>Email:</strong> {otpData.user?.emailAddress}</p>
                      <p className="mb-0"><strong>UUID:</strong> {otpData.user?.uuid}</p>
                    </div>
                    {otpData.sessions && otpData.sessions.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>OTP</th>
                              <th>Expires</th>
                              <th>Retries</th>
                              <th>Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {otpData.sessions.map((session, index) => (
                              <tr key={index}>
                                <td><code className="text-primary">{session.otp}</code></td>
                                <td>{session.otp_expires ? formatDateTime(session.otp_expires) : '—'}</td>
                                <td>{session.retries ?? '—'}</td>
                                <td>{session.createdAt ? formatDateTime(session.createdAt) : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted">No active sessions found.</p>
                    )}
                  </>
                ) : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeOtpModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showGrantTrialModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Grant Free Trial</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeGrantTrialModal}
                  disabled={grantTrialSaving}
                ></button>
              </div>
              <div className="modal-body">
                {grantTrialLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="mb-3">
                      How many months of Free Trial would you like to grant to this user?{' '}
                      <span className="text-muted small">Maximum: 2 months.</span>
                    </p>
                    <input
                      type="number"
                      min="0"
                      max="2"
                      className="form-control"
                      step="1"
                      value={grantTrialMonths}
                      onChange={(event) => setGrantTrialMonths(event.target.value)}
                      disabled={grantTrialSaving}
                    />
                    <div className="form-text">Enter a value between 0 and 2.</div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeGrantTrialModal}
                  disabled={grantTrialSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={submitGrantTrial}
                  disabled={grantTrialLoading || grantTrialSaving}
                >
                  {grantTrialSaving ? 'Saving...' : 'Grant Trial'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showVersionsModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Profile Versions</h5>
                <button type="button" className="btn-close" onClick={closeVersionsModal}></button>
              </div>
              <div className="modal-body">
                {versionsLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : versionsData?.error ? (
                  <div className="alert alert-danger mb-0">{versionsData.error}</div>
                ) : versionsData ? (
                  <>
                    <div className="mb-3">
                      <p className="mb-1">
                        <strong>Email:</strong> {versionsData.userEmail || '—'}
                      </p>
                      <p className="mb-0">
                        <strong>Profile:</strong> {versionsData.profileLabel || '—'}
                      </p>
                    </div>
                    {versionsData.history?.length > 0 ? (
                      <div>
                        {versionsData.history.map((entry) => {
                          const changes = getProfileHistoryChanges(entry.oldData, entry.newData);
                          const initialEntry = isInitialProfileEntry(entry.oldData);
                          return (
                            <div key={entry.id} className="history-entry">
                              <div className="d-flex flex-column flex-sm-row justify-content-between gap-1 mb-2">
                                <span className="fw-semibold">
                                  {entry.createdAt ? formatDateTime(entry.createdAt) : '—'}
                                </span>
                                <span className="text-muted small">
                                  Changed by {entry.changedBy?.email || '—'}
                                </span>
                              </div>
                              {initialEntry ? (
                                <p className="text-muted small mb-2">
                                  First recorded profile after BAA acceptance.
                                </p>
                              ) : null}
                              {changes.length > 0 ? (
                                <ul className="history-change-list mb-0">
                                  {changes.map((change) => (
                                    <li key={change.label}>
                                      <strong>{change.label}:</strong>{' '}
                                      {initialEntry ? (
                                        <span>{change.new}</span>
                                      ) : (
                                        <span>
                                          {change.old} → {change.new}
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-muted small mb-0">No field changes recorded.</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-muted mb-0">No profile changes recorded yet.</p>
                    )}
                  </>
                ) : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeVersionsModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showChargeCustomerModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Charge Customer</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeChargeCustomerModal}
                  disabled={chargeCustomerSubmitting}
                ></button>
              </div>
              <div className="modal-body">
                {chargeCustomerLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Choose Plan</label>
                      <select
                        className="form-select"
                        value={selectedPlanUuid}
                        onChange={(event) => setSelectedPlanUuid(event.target.value)}
                        disabled={chargeCustomerSubmitting}
                      >
                        <option value="">Select a plan</option>
                        {billingPlans.map((plan) => (
                          <option key={plan.uuid} value={plan.uuid}>
                            {plan.plan_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeChargeCustomerModal}
                  disabled={chargeCustomerSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={submitChargeCustomer}
                  disabled={chargeCustomerLoading || chargeCustomerSubmitting}
                >
                  {chargeCustomerSubmitting ? 'Proceeding...' : 'Proceed to Billing'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="users-view">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
          <div>
            <h1 className="display-6 fw-semibold mb-1">User Management</h1>
            <p className="text-muted mb-0">
              Monitor user lifecycle, subscription status, and financial activity.
            </p>
          </div>
        </div>

        <div className="card-soft mb-4 p-3">
          <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center">
            <input
              className="form-control rounded-pill shadow-sm flex-grow-1"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <input
              type="date"
              className="form-control rounded-pill shadow-sm"
              style={{ maxWidth: 180 }}
              value={dateSearch}
              onChange={(e) => {
                setDateSearch(e.target.value);
                onDateChange?.(e.target.value);
              }}
              disabled={loading}
              title="Filter by created date"
            />
            <select
              className="form-select rounded-pill shadow-sm"
              style={{ maxWidth: 180 }}
              value={statusFilter}
              onChange={(event) => onStatusChange?.(event.target.value)}
              disabled={loading}
            >
              <option value="all">All statuses</option>
              <option value="pending">pending</option>
              <option value="unpaid">unpaid</option>
              <option value="reviewing">reviewing</option>
              <option value="active">active</option>
              <option value="beta">beta</option>
              <option value="free_minutes">free minutes</option>
            </select>
            <select
              className="form-select rounded-pill shadow-sm"
              style={{ maxWidth: 180 }}
              value={sortFilter}
              onChange={(event) => onSortChange?.(event.target.value)}
              disabled={loading}
            >
              <option value="createdAt_desc">Newest first</option>
              <option value="createdAt_asc">Oldest first</option>
              <option value="name_asc">A → Z</option>
            </select>
          </div>
        </div>

      <div className="card-soft">
          <table className="table align-middle mb-0 users-table">
            <thead>
              <tr>
                <th scope="col" className="text-muted fw-semibold" style={{ width: '8%' }}>OTP</th>
                <th scope="col" className="text-muted fw-semibold" style={{ width: '32%' }}>User</th>
                <th scope="col" className="text-muted fw-semibold" style={{ width: '10%' }}>Updates</th>
                <th scope="col" className="text-muted fw-semibold" style={{ width: '18%' }}>Status</th>
                <th scope="col" className="text-muted fw-semibold" style={{ width: '12%' }}>Agreements</th>
                <th scope="col" className="text-muted fw-semibold" style={{ width: '20%' }}>Dates</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    Loading users…
                  </td>
                </tr>
              ) : users.length ? (
                users.map((user) => {
                  const profiles = getUserProfiles(user);
                  return (
                  <tr key={user.id || user.uuid} className="rounded-4 shadow-sm">
                    <td>
                      <span onClick={() => viewOtp(user.uuid)} className="badge rounded-pill pointer shadow-sm bg-primary-subtle text-primary text-uppercase">
                        View OTP
                      </span>
                    </td>
                    <td>
                      <div className="user-email fw-medium">{user.emailAddress || '—'}</div>
                      {profiles.length > 0 ? (
                        profiles.map((profile) => (
                          <div key={profile.uuid || profile.id} className="user-profile-line text-muted small">
                            {formatProfileSummary(profile)}
                          </div>
                        ))
                      ) : (
                        <div className="user-profile-line text-muted small">No profile</div>
                      )}
                    </td>
                    <td>
                      {profiles.length > 0 ? (
                        profiles.map((profile, index) => (
                          <div
                            key={profile.uuid || profile.id || index}
                            className={index > 0 ? 'profile-versions-row' : ''}
                          >
                            {profile.uuid ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 text-decoration-none"
                                onClick={() => openVersionsModal(profile, user)}
                                disabled={loading}
                              >
                                View Logs
                              </button>
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                          </div>
                        ))
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex flex-column align-items-start gap-2">
                        <StatusBadge status={user.currentStatus} />
                        {user.isEnabled === false && (
                          <span className="badge rounded-pill bg-danger-subtle text-danger fw-semibold">
                            Disabled
                          </span>
                        )}
                        <button
                          type="button"
                          className={`btn btn-sm rounded-pill ${user.isEnabled === false ? 'btn-outline-success' : 'btn-outline-danger'}`}
                          onClick={() => handleToggleEnabled(user)}
                          disabled={loading || togglingUuid === user.uuid}
                        >
                          {togglingUuid === user.uuid
                            ? 'Working…'
                            : user.isEnabled === false ? 'Enable' : 'Disable'}
                        </button>
                        {user.isFreeMinutes && (
                          <span className="badge rounded-pill bg-info-subtle text-info fw-semibold">
                            {`Free Minutes · ${user.topupMinutesBalance ?? 0} left`}
                          </span>
                        )}
                        {user.currentStatus === 'unpaid' && (
                          (user?.profile?.days_grant >= 1) ? (
                            <div className="small">
                              <span className="badge bg-success-subtle text-success fw-semibold me-1">Trial Granted</span>
                              <span className="text-muted">
                                {`Trial months granted: ${user.profile.days_grant}`}
                              </span>
                            </div>
                          ) : (
                            <div className="d-flex gap-2 flex-wrap">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary rounded-pill"
                                onClick={() => openGrantTrialModal(user)}
                                disabled={loading}
                              >
                                Grant Trial
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success rounded-pill"
                                onClick={() => openChargeCustomerModal(user)}
                                disabled={loading}
                              >
                                Charge Customer
                              </button>
                            </div>
                          )
                        )}
                        {user.currentStatus === 'beta' && (
                          <div className="d-flex gap-2 flex-wrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary rounded-pill"
                              onClick={() => openGrantTrialModal(user)}
                              disabled={loading}
                            >
                              Grant Trial
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success rounded-pill"
                              onClick={() => openChargeCustomerModal(user)}
                              disabled={loading}
                            >
                              Charge Customer
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="legal-stack text-muted">
                        <span>ToS: {formatAgreement(user.acceptedTos)}</span>
                        <span>AI: {formatAgreement(user.acceptedAI)}</span>
                        <span>BAA: {formatAgreement(user.acceptedBaa)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="dates-stack text-muted">
                        <span>
                          <span className="text-muted">Created:</span>{' '}
                          {user.createdAt ? formatDateTime(user.createdAt) : '—'}
                        </span>
                        {/* <span>
                          <span className="text-muted">Updated:</span>{' '}
                          {user.updatedAt ? formatDateTime(user.updatedAt) : '—'}
                        </span> */}
                      </div>
                    </td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    No users match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        {pagination && (
          <div className="card-footer bg-transparent border-0 px-4 py-3">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
              <small className="text-muted">
                Showing {rangeStart} to {rangeEnd} of {totalUsers} users
              </small>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary rounded-pill"
                  onClick={() => onPageChange?.(currentPage - 1)}
                  disabled={loading || currentPage <= 1}
                >
                  Previous
                </button>
                <div className="d-flex align-items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 10) {
                      pageNum = i + 1;
                    } else if (currentPage <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 4) {
                      pageNum = totalPages - 9 + i;
                    } else {
                      pageNum = currentPage - 4 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        type="button"
                        className={`btn btn-sm rounded-pill ${currentPage === pageNum ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => onPageChange?.(pageNum)}
                        disabled={loading || currentPage === pageNum}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary rounded-pill"
                  onClick={() => onPageChange?.(currentPage + 1)}
                  disabled={loading || currentPage >= totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default UserManagement;
